import { useEffect, useState, type ReactNode } from 'react';
import { type RegraFreteNet } from '@triade/contracts';
import { usePlanoParametros, useSalvarPlanoParametros } from '@/api/plano';
import { usePode } from '@/api/me';
import { ErroLista } from '@/components/cargos';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { montarParametros, type ParametrosForm, parametrosFormDe } from './plano-config-form';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

const ROTULO_REGRA: Record<RegraFreteNet, string> = { LEGAL: 'Legal (frete net pela regra legal)', FATOR_FIXO: 'Fator fixo (frete x fator)' };

/**
 * Configuracoes do plano de vendas (/configuracoes/plano): parametros do plano. Tokens e API ficam em /configuracoes/integracoes.
 * Valores vem da API; a tela so converte o texto digitado (percentual e decimal) pelos helpers do contrato.
 */
export function PlanoConfigPage() {
  return <ParametrosPlano />;
}

// ---------- Parametros do plano ----------

function ParametrosPlano() {
  const { pode } = usePode();
  const podeEditar = pode('plano.editar');
  const parametros = usePlanoParametros();
  const salvar = useSalvarPlanoParametros();
  const [form, setForm] = useState<ParametrosForm | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const dados = parametros.data;

  useEffect(() => {
    if (dados) setForm(parametrosFormDe(dados));
  }, [dados]);

  function alterar<K extends keyof ParametrosForm>(campo: K, valor: ParametrosForm[K]) {
    setForm((atual) => (atual ? { ...atual, [campo]: valor } : atual));
  }

  async function salvarParametros() {
    if (!form) return;
    setErro(null);
    setAviso(null);
    const montagem = montarParametros(form);
    if (!montagem.ok) {
      setErro(montagem.mensagem);
      return;
    }
    try {
      await salvar.mutateAsync(montagem.dto);
      setAviso('Parâmetros salvos.');
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  let corpo: ReactNode;
  if (parametros.isError && !dados) corpo = <ErroLista erro={parametros.error} onTentar={() => void parametros.refetch()} titulo="Não foi possível carregar os parâmetros" />;
  else if (!form) corpo = <p role="status" className="px-5 py-6 text-small text-ink-muted">Carregando parâmetros…</p>;
  else {
    const fatorDesabilitado = form.regraFreteNet !== 'FATOR_FIXO' || !podeEditar;
    const campoPct = (id: keyof ParametrosForm, rotulo: string) => (
      <div>
        <label htmlFor={`param-${id}`} className={ROTULO}>{rotulo}</label>
        <div className="flex items-center gap-1.5">
          <input id={`param-${id}`} inputMode="decimal" autoComplete="off" className={`${CAMPO} w-[110px] tabular-nums`} disabled={!podeEditar} value={String(form[id])} onChange={(e) => alterar(id, e.target.value)} />
          <span className="text-small text-ink-muted">%</span>
        </div>
      </div>
    );
    corpo = (
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void salvarParametros();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="param-regraFreteNet" className={ROTULO}>Regra do frete net</label>
            <select id="param-regraFreteNet" className={`${CAMPO} w-full`} disabled={!podeEditar} value={form.regraFreteNet} onChange={(e) => alterar('regraFreteNet', e.target.value as RegraFreteNet)}>
              {(Object.keys(ROTULO_REGRA) as RegraFreteNet[]).map((r) => (
                <option key={r} value={r}>{ROTULO_REGRA[r]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="param-fatorFreteNet" className={ROTULO}>Fator do frete net (0 a 1)</label>
            <input id="param-fatorFreteNet" inputMode="decimal" autoComplete="off" className={`${CAMPO} w-full tabular-nums`} disabled={fatorDesabilitado} value={form.fatorFreteNet} placeholder={form.regraFreteNet === 'FATOR_FIXO' ? '0,9' : '—'} onChange={(e) => alterar('fatorFreteNet', e.target.value)} />
          </div>
          {campoPct('freteIcmsPct', 'ICMS do frete')}
          {campoPct('pesoPrevisao', 'Peso da Previsão no total do mês')}
          {campoPct('icmsPadrao', 'ICMS padrão')}
          {campoPct('pisPadrao', 'PIS padrão')}
          {campoPct('cofinsPadrao', 'COFINS padrão')}
          {campoPct('comissaoPadrao', 'Comissão padrão')}
          <div>
            <label htmlFor="param-saldoInicialFluxo" className={ROTULO}>Saldo inicial do fluxo (R$)</label>
            <input id="param-saldoInicialFluxo" inputMode="decimal" autoComplete="off" className={`${CAMPO} w-full tabular-nums`} disabled={!podeEditar} value={form.saldoInicialFluxo} onChange={(e) => alterar('saldoInicialFluxo', e.target.value)} />
          </div>
        </div>
        <div className="flex flex-col gap-2.5 text-small">
          <label className="flex items-center gap-2">
            <input type="checkbox" className="size-4" checked={form.empresaNaoCumulativa} disabled={!podeEditar} onChange={(e) => alterar('empresaNaoCumulativa', e.target.checked)} />
            Empresa não cumulativa (PIS e COFINS)
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="size-4" checked={form.pendenteNaNecessidade} disabled={!podeEditar} onChange={(e) => alterar('pendenteNaNecessidade', e.target.checked)} />
            Considerar pedidos pendentes na necessidade de MP
          </label>
        </div>
        {podeEditar && (
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={salvar.isPending} className="h-[38px] rounded-[10px] px-4">
              {salvar.isPending ? 'Salvando…' : 'Salvar parâmetros'}
            </Button>
            {erro && <p role="alert" className="text-small text-danger-ink">{erro}</p>}
            {aviso && <p role="status" className="text-small text-ink-secondary">{aviso}</p>}
          </div>
        )}
        {!podeEditar && <p className="text-caption text-ink-muted">Somente leitura: editar os parâmetros exige a permissão de editar o plano.</p>}
      </form>
    );
  }

  return (
    <section aria-labelledby="titulo-parametros-plano" className="rounded-2xl border border-border bg-card p-5">
      <h2 id="titulo-parametros-plano" className="text-body font-semibold">Parâmetros do plano</h2>
      <p className="mb-4 mt-1 text-small text-ink-secondary">Regra do frete, impostos padrão, comissão e saldo inicial do fluxo.</p>
      {corpo}
    </section>
  );
}
